/**
 * A record's related items — the SHARED Connections surface, in its workspace
 * presentation (#1466).
 *
 * ── What this file used to be, and the defect that closed it ───────────────
 * Christopher, 2026-08-07: *"that related screen in the detail form. Remove all
 * that tab stuff and replace it with the cascade list we already have but with
 * one difference: you put everything that has a child record first and you
 * expand these."* That was done, and it was right — but it was done HERE, as a
 * fourth answer to "what is this record connected to", beside the console
 * drawer's, the portal drawer's and `PartyRelations`'. Three of the four
 * disagreed about what a group even was, so the same record answered "how many
 * People?" differently depending on which surface you asked
 * (`packages/experience/src/connections.ts` has the measurement).
 *
 * So the arrangement, the counts, the chips, the anchors, the lens and the
 * chrome are now `@solum/ui`'s `Connections` — the same component the two
 * drawers render — and what stays here is what only the record page can
 * supply: the registered SLOTS, the governed lookup, and the relationship GRID.
 *
 * ── Why the group here is the SLOT ─────────────────────────────────────────
 * `groupConnections` keys a group `<relType>:<reading>`, which is the right
 * grain for a panel that summarises a record. It is the wrong grain for the
 * surface you WORK on: `core.related_order` states positions per slot,
 * `relationship_frequency` counts per slot, `sections[].related` declares per
 * slot, and an Add writes into exactly one far entity. `recordConnections.ts`
 * explains the rest, including why building this page out of edges alone would
 * have deleted every FK-stored related list and every empty relationship's Add.
 *
 * ── What the shared component took over, and what went with it ─────────────
 * Two things this file used to do are gone rather than reimplemented:
 *
 *  · the per-section COLLAPSE. The read surface now spends space on evidence:
 *    populated and settled-unknown slots stay visible, while a server-measured
 *    zero is quiet. Every registered slot — including those quiet zeros — stays
 *    searchable through the one global Add relationship action, so hiding an
 *    empty section never hides the capability to start its first relationship.
 *  · the "Related" bar's own label, replaced by the surface's heading and its
 *    honest total. A person's own SECTIONS (`groupSlots`) survive as one
 *    Connections each, headed by the name they gave it.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { LayoutGrid, Maximize2, Minimize2, Network, Plus, Rows3, Table2 } from 'lucide-react'
import { Button, CollectionWorkspace, Connections, CustomizeRowMenu, GridSearch, Modal, RelationshipGraph, SavedViewSelector, SegmentedControl, ViewSettingsMenu, type SavedViewSelectorProps } from '@solum/ui'
import type { ConnectionGroup, RelatedPresentation } from '@solum/experience'
import { displayLabel, useEntityIdentities, useSchema } from '../data/queries'
import { queryGate } from '../data/QueryBoundary'
import { listSlots } from './relations'
import { BandGrid } from './RelationBand'
import { RECORD_COLUMN, useRelationCollection, type RelationView } from './relationCollection'
import type { RelationSlot } from './relations'
import { useRelatedCounts, useRelationRows } from './useRelations'
import { useSlotFrequency } from './useSlotFrequency'
import { RELATED_GROUP, groupSlots, useRelatedOrder } from './useRelatedOrder'
import { PersonalizeRelated } from './PersonalizeRelated'
import { arrangeCascade, declaredPositions, type DeclaredRelated } from './declaredRelated'
import { RelationshipWorkspace } from './RelationshipWorkspace'
import type { GraphLayout } from '@solum/experience'
import { PeekRow } from './PeekRow'
import { useConnections } from './useConnections'
import { useConnectionsLens } from './useConnectionsLens'
import { useGraphHops } from './useGraphHops'
import { useRelationshipWorkspace } from './useRelationshipWorkspace'
import { useNestedViewChoice } from './useNestedViewChoice'
import { useRelatedPresentation } from './useRelatedPresentation'
import { RelationMap } from './RelationMap'
import { RelationTimeline } from './RelationTimeline'
import type { PeekRecipe } from '@solum/experience'
import {
  slotConnectionGroups, slotNeedsRows, visibleRelationshipGroups, visibleRelationshipSections,
} from './recordConnections'
import { useDefaultColumns } from '../list/defaultColumns'
import { useViews } from '../experience/governed'
import { openPalette } from '../app/paletteScope'
import { relationshipWriteOffered, slotOffersAdd } from './relations'
import { openQuickCreate } from './quickCreate'
import s from './RelationCascade.module.css'

export interface RelationCascadeProps {
  entity: string
  id: string
  /** how to name this record in a create header */
  label?: string
  /** open a related record — the split pane routes it back into itself */
  onOpenRecord?: (entity: string, id: string, name?: string) => void
  /**
   * A slot the record's ⌘ asked for ("Add to People"). The palette's verb is an
   * ADD, so it opens that slot's governed lookup — it used to merely expand the
   * section and leave the person to find the button, which is the verb doing
   * four fifths of what it promised.
   * Owned by `RecordBody` because that is where the record's palette button is.
   */
  forced?: { slot: string; sequence: number } | null
  /**
   * Related lists the FORM DOCUMENT asks for (`sections[].related`, governed by
   * lint rule 6 since `2026080301`). They lead the cascade, in document order,
   * under the titles the definition wrote; everything else follows in the
   * derived order. Absent — and it is absent for every entity whose form does
   * not declare one — this renders exactly what it rendered before.
   *
   * NOT an allow-list: see `arrangeCascade`. A definition says what matters
   * here, not what exists, and the derived slots are what make a new module's
   * relationship appear with no console change.
   */
  declared?: DeclaredRelated[]
}

export interface RegisteredRelationSectionProps {
  slot: RelationSlot
  recordId: string
  parentEntity: string
  parentLabel?: string
  onOpenRecord?: (entity: string, id: string, name?: string) => void
  title?: string
  description?: string
  count?: string | null
  defaultOpen?: boolean
  collapsible?: boolean
  openSignal?: number
  testId?: string
  headTestId?: string
}

export function RelationCascade({ entity, id, label, onOpenRecord, forced, declared }: RelationCascadeProps) {
  const schema = useSchema()
  const slots = useMemo(() => listSlots(schema.data, entity), [schema.data, entity])
  /* The far entity's governed glyph for each type header (#2349 §D2).
     `core.entity_identity.icon_key`, the same registry the palette and the
     navigation already read — not a second icon vocabulary, and not a guess
     derived from the first row of a group. */
  const identities = useEntityIdentities()
  const iconOf = useMemo(() => {
    const byEntity = new Map((identities.data ?? []).map((i) => [i.entity_type, i.icon_key]))
    return (target: string) => byEntity.get(target) ?? null
  }, [identities.data])
  const counts = useRelatedCounts(entity, id, slots.length > 0)
  const { bySlot } = counts
  const freq = useSlotFrequency(entity)
  const order = useRelatedOrder(entity)
  /* The PERSON's request and the GOVERNED answer are two different things, and
     keeping them apart is what makes "a personal choice overrides without
     changing the entity default" true rather than hoped for. The request is a
     `core.user_setting`; the answer is the server's, and it is the one that
     decides what is drawn. */
  const { requestedFor, setLens } = useConnectionsLens(entity)
  /* The WHOLE-RECORD reading, in its own store and its own parameter (#2324).
     A different key from `connections.lens` all the way down, because the
     ruling forbids reinterpreting one as the other and two values that never
     share a container cannot be confused by a later edit. */
  const workspace = useRelationshipWorkspace(entity)
  /* THE ONE SWITCH (#2406 owner ruling 80629 — "whatever is most Apple /
     Nespresso-like"): Cards | Grid | Graph at the top of Connections, and no
     icon strip on every plate. Cards / Grid is how EVERY plate reads its rows,
     remembered per person and entity type in this browser — a viewer's
     convenience, never a fact about a record (keyed by TYPE, not uuid, §8).
     null until the person touches it, so governed defaults stand until then. */
  const readKey = `solum.connections.read.${entity}`
  const [surfaceRead, setSurfaceReadState] = useState<'grid' | 'cards' | null>(() => {
    try {
      const v = localStorage.getItem(readKey)
      return v === 'grid' || v === 'cards' ? v : null
    } catch { return null }
  })
  const setSurfaceRead = (v: 'grid' | 'cards') => {
    setSurfaceReadState(v)
    try { localStorage.setItem(readKey, v) } catch { /* storage refused: the choice lasts this visit */ }
  }
  /* The slots actually ON THIS SURFACE, not every registered one.

     An organisation has ten registered relationships and a Havenkantoor-shaped
     record has rows in exactly one of them; asking about the other nine would
     resolve nine documents for sections nobody is looking at. The predicate is
     `relationshipGroupIsVisible`'s, so this is derived from the same rule that
     decides what is DRAWN rather than from a second one that could drift.

     Each entry carries its target explicitly. The slot identity contains the
     far entity — `<relType>:<direction>:<otherEntity>` — and the resolver
     refuses to read it back out of the key, because a renamed relationship type
     would then become a silent capability change. */
  const visibleSlots = useMemo(() => slots
    .filter((slot) => {
      const counted = bySlot.get(slot.id)
      return counted === undefined || counted.n > 0
    })
    .map((slot) => ({ slot: slot.id, target: slot.otherEntity })), [slots, bySlot])
  const requested = useMemo(
    () => requestedFor(visibleSlots.map((r) => r.slot)), [requestedFor, visibleSlots])
  const presentation = useRelatedPresentation(entity, visibleSlots, requested, workspace.requested)
  const mode = presentation.workspace.mode
  const layout = presentation.workspace.graph.layout
  /* What ONE plate draws (ruling 80629), in one place so the plate's header
     and its body cannot disagree: a plate someone explicitly set to a SPECIAL
     reading (map, timeline, peek — in its view menu) keeps it; otherwise every
     plate follows the one switch once it has been touched; otherwise the
     governed default stands. */
  const drawnLens = (slotId: string): RelatedPresentation => {
    const one = presentation.of(slotId)
    const special = one.active !== 'grid' && one.active !== 'cards'
    const chosenSpecial = special && one.provenance.activeSource === 'requested'
    if (chosenSpecial || surfaceRead == null) return one.active
    return one.available.includes(surfaceRead) ? surfaceRead : one.active
  }
  /* The graph is read ONLY in Graph mode, and only ever one hop at a time.
     §4: "one hop per user action… no eager recursive crawl." In List mode the
     door is not called at all — a picture nothing draws is not paid for. */
  const graph = useGraphHops(entity, id, mode === 'graph')

  /* The PEEK recipe is a property of the TARGET ENTITY and of nothing on the
     edge — core 2026091521 says so in as many words, and that is what makes it
     findable from any slot that draws the same entity. A node at two hops has no
     slot on this page at all; #2281's own fallback answers for it (the first
     visible, non-empty section of the resolved form), which is the canonical
     behaviour rather than a degradation. */
  const peekRecipeFor = (target: string) => {
    for (const ref of visibleSlots) {
      if (ref.target === target) {
        const recipe = presentation.of(ref.slot).peek
        if (recipe) return recipe
      }
    }
    return null
  }

  /* The mode and layout switchers, rendered in BOTH modes so the way back is
     always where the way out was. Each offers only what the deployment offers
     AND this build can draw, and a single offered value draws no control at all
     — the same rule a one-lens section follows, for the same reason: a control
     with one option is ornament that still takes a tab stop. */
  /* SESSION state, like the selection and the preview beside it — a person's
     window is not a fact about the record, and persisting it keyed by record
     uuid is what §8 refuses. */
  const [expanded, setExpanded] = useState(false)
  /* The element focus returns to when the dialog closes. The button that opened
     it is INSIDE the dialog's own copy of the switcher strip, so it is unmounted
     by the time the dialog closes — the same defect `UserMenu`'s `triggerRef`
     exists for, one surface over. This ref is on whichever strip is mounted, and
     the one that survives closing is the inline one. */
  const expandBtn = useRef<HTMLButtonElement>(null)

  /* Graph mode is withdrawn while the cascade is being PERSONALIZED, so a
     full-screen dialog cannot outlive the surface it is about. */

  const switchers = (
    <div className={s.workspaceModes} data-testid="relationship-workspace-modes">

      {/* Cards | Grid | Graph — the design's switch, one control. Graph is
          offered only where the governed workspace offers it. */}
      <SegmentedControl<'cards' | 'grid' | 'graph'>
        value={mode === 'graph' ? 'graph' : (surfaceRead ?? 'grid')}
        onChange={(v) => {
          if (v === 'graph') { workspace.setMode('graph'); return }
          if (mode === 'graph') workspace.setMode('list')
          setSurfaceRead(v)
        }}
        presentation="both"
        options={[
          { value: 'cards' as const, label: 'Cards', icon: <Rows3 size={12} aria-hidden /> },
          { value: 'grid' as const, label: 'Grid', icon: <Table2 size={12} aria-hidden /> },
          ...(presentation.workspace.modes.includes('graph')
            ? [{ value: 'graph' as const, label: 'Graph', icon: <Network size={12} aria-hidden /> }]
            : []),
        ]}
        label="Show relationships as" size="sm" testId="relationship-workspace-mode" />
      {/* §8: "Provide 'Reset to governed default'." Offered only where there IS
          a personal choice to drop — a control that undoes nothing is a control
          that teaches people it does nothing. */}
      {workspace.requested && (
        /* The SHARED Button, not a raw one. `experience-adoption.test.ts`
           counts raw host buttons as debt that only shrinks, and it caught this
           the first time it was written — which is the ratchet doing exactly
           its job: a one-off control is how a host regrows a control kit. */
        <Button variant="ghost" size="sm" data-testid="relationship-workspace-reset"
          onClick={workspace.reset}>Reset to default</Button>
      )}
      {mode === 'graph' && presentation.workspace.graph.layouts.length > 1 && (
        <SegmentedControl<GraphLayout>
          value={layout} onChange={workspace.setLayout}
          options={presentation.workspace.graph.layouts.map((l) => ({
            value: l, label: l === 'levels' ? 'Levels' : l === 'metro' ? 'Metro' : 'Network',
          }))}
          label="Lay the graph out as" size="sm" testId="relationship-workspace-layout" />
      )}
      {/* FULL SCREEN, and the way back is where the way out was (#2349 §E,
          mock item 33). The SAME `RelationshipGraph` is mounted either way —
          `graphWorkspace` below builds it once — so there is one implementation
          and one set of verbs rather than an inline picture and a second
          full-screen one that drift.

          LAST in the strip, and that came from looking at the regenerated
          raster: it was first, which put a window action ahead of the choice of
          what to look at. The strip now reads as the view decisions and then
          what to do with the window — List | Graph, Network | Metro, reset, and
          only then full screen.

          Rendered only in Graph mode: expanding a list is not a thing this
          control does, and a button that means nothing where it sits is the
          dead control this strip already refuses elsewhere. */}
      {mode === 'graph' && (
        <Button ref={expandBtn} variant="ghost" size="sm"
          data-testid="relationship-graph-expand"
          aria-label={expanded ? 'Exit full screen' : 'Show the graph full screen'}
          onClick={() => setExpanded((v) => !v)}>
          {/* `aria-hidden`, and it is not a formality. A lucide glyph without it
              is exposed as `role="img"`, and `record-graph-workspace.spec.ts`
              found the consequence: `graph.getByRole('img')` resolved to TWO
              elements — this decorative mark and the canvas whose accessible
              name is the graph's only description. A decorative icon inside a
              labelled button must announce nothing. */}
          {expanded
            ? <Minimize2 size={14} aria-hidden />
            : <Maximize2 size={14} aria-hidden />}
          {expanded ? 'Exit full screen' : 'Full screen'}
        </Button>
      )}
    </div>
  )
  /* The edges used to be read for the METRO lens. #2324 retired that lens: Metro
     describes how a record's WHOLE relationship network is laid out, not how one
     list is rendered, so no section asks for a traversal any more and none of
     the four remaining lenses needs one — Grid, Cards, Map and Peek all draw
     from each slot's own rows.

     The read stays wired and OFF rather than deleted, because its remaining
     consumer is the Graph mode of this workspace, which arrives with the
     governed one-hop door. Reading edges now would pay for a picture nothing
     draws; deleting the call would lose the `problem` this surface reports. */
  const connections = useConnections(entity, id, false)
  const [editing, setEditing] = useState(false)
  /* Graph mode is withdrawn while the cascade is being PERSONALIZED, and a
     window is not a fact about the record — so a full-screen dialog cannot
     outlive either the mode or the surface it is about. Declared here because it
     reads `editing`; the state itself sits above `switchers`, which is the first
     thing that draws the control. */
  useEffect(() => { if (mode !== 'graph' || editing) setExpanded(false) }, [mode, editing])
  /* SESSION state, never written anywhere. §8: "Expansion/selection/root state
     is session state", and persisting a selection keyed by record uuid is
     exactly what that clause refuses. */
  const [selectedNode, setSelectedNode] = useState<string | null>(null)
  /* PEEK is session state too, and it is a SEPARATE piece from the selection:
     §4 offers Peek as a verb on a selected node, so selecting is not previewing
     and closing the preview does not deselect. */
  const [peeking, setPeeking] = useState<{ entity: string; id: string } | null>(null)
  const personalize = useRef<HTMLButtonElement>(null)
  /* Which slot's governed lookup to open. A rising counter rather than a bare
     id because `RelationAdd` opens on a CHANGE — the same slot asked for twice
     has to open twice. */
  const [adding, setAdding] = useState<{ slot: string; n: number }>({ slot: '', n: 0 })
  /* Which saved view each relationship reads through. SESSION state, like the
     lens beside it on the plate: the chip in the plate's header chooses it and
     the grid under it reads it, so it lives with the one component that draws
     both. */
  const [viewBySlot, setViewBySlot] = useState<Record<string, string>>({})

  useEffect(() => {
    if (forced) setAdding({ slot: forced.slot, n: forced.sequence })
  }, [forced])

  /* The person's STATED rung first, then the learned one. `groupSlots` drops
     what they hid, honours the sections they made, and hands each section to
     `orderSlots` — which answers with the old populated → frequency → alpha
     order for every slot the statement is silent about. Somebody who has never
     opened the editor therefore sees exactly what they saw before, to the row.
     `arrangeConnections` preserves that order rather than re-sorting it. */
  const declarations = useMemo(() => declared ?? [], [declared])
  const decorations = useMemo(
    () => new Map(arrangeCascade(slots, declarations).map((e) => [e.slot.id, e] as const)),
    [slots, declarations])
  const sections = useMemo(() => {
    const positions = declaredPositions(slots, declarations)
    return groupSlots(slots, order.rung,
      (sid) => bySlot.get(sid)?.n ?? null,
      freq.countOf,
      (sid) => positions.get(sid) ?? null)
  }, [slots, declarations, order.rung, bySlot, freq.countOf])

  const slotById = useMemo(() => new Map(slots.map((slot) => [slot.id, slot])), [slots])
  /* ONE answer to "may this slot be added to here?", read by the List plates,
     the Levels cards and the record palette's verbs (#2849): `slotOffersAdd`,
     the slot's own create/link offer, which `deriveRelationSlots` has already
     narrowed to what core admits for the written entity's door. */
  const offersAdd = (slotId: string): boolean => {
    const slot = slotById.get(slotId)
    return !!slot && slotOffersAdd(slot)
  }
  /* …and the record-level Add relationship opens over every slot, so it is
     drawn only when one of them offers Add — with none, its dialog listed each
     type "Not offered" (#2849 review). */
  const anyAdd = slots.some(slotOffersAdd)
  const groupsOf =(sectionSlots: RelationSlot[]): ConnectionGroup[] => slotConnectionGroups(
    sectionSlots, connections.groups, (sid) => bySlot.get(sid),
    (sid) => decorations.get(sid)?.title,
    iconOf)

  /** One visible group's rows. Add belongs once to the whole workspace. */
  const bodyOf = (group: ConnectionGroup) => {
    const slot = slotById.get(group.key)
    if (!slot) return null
    const decorated = decorations.get(slot.id)
    return (
      <>
        {decorated?.description && <p className={s.description}>{decorated.description}</p>}
        {/* An UNKNOWN count still reads and gets the ordinary body with its
            spinner. In Metro, an already-loaded edge wins over a stale zero;
            grid/cards do not claim that witness because they load slot rows. */}
        {bySlot.get(slot.id)?.n === 0 && group.items.length === 0 ? null : (
          <RelationSectionBody slot={slot} recordId={id} parentEntity={entity} parentLabel={label}
            read={slotNeedsRows(bySlot.get(slot.id)) || group.items.length > 0}
            countKnown={bySlot.has(slot.id)} lens={drawnLens(slot.id)}
            peekRecipe={presentation.of(slot.id).peek}
            bare viewId={viewBySlot[slot.id] ?? ''}
            onOpenRecord={onOpenRecord} />
        )}
      </>
    )
  }

  /* #1466 already ordered the sections and slots. Filtering may remove a
     known-empty group, but never sorts what remains. Empty authored sections
     disappear so a heading never owns a blank acre of page. */
  const visibleSections = visibleRelationshipSections(sections
    .map((section) => ({ ...section, groups: visibleRelationshipGroups(groupsOf(section.slots)) })))
  const shown = visibleSections.length > 0
    ? visibleSections
    : [{ label: RELATED_GROUP, slots: [] as RelationSlot[], groups: [] as ConnectionGroup[] }]

  /* EVERY section on this page is derived from the schema, so an unresolved
     schema read is not "no relationships" — it is "not known yet", and the two
     have to be different sentences (#389). `queryGate` says which of loading /
     refused / failed it is; it returns null once the read is ready, and the
     surface below then answers for itself. */
  /* Counts are OPTIONAL data but REQUIRED state. A settled response may omit a
     slot (genuine unknown); while the request is loading or failed, rendering
     all missing keys as unknown would mount one row query per slot and flash a
     page of groups that may be measured zero a moment later. */
  /* THE GRAPH, BUILT ONCE (#2349 §E).
   *
   * *"Reuse one `RelationshipGraph` inline and full-screen. Expand opens that
   * same component in an accessible dialog/overlay."* So this is a value, not
   * two call sites: the inline pane renders it, and the dialog renders the same
   * expression. A second `<RelationshipGraph …>` with its own verbs is exactly
   * the two-implementations outcome the ruling forbids, and it is the kind that
   * looks harmless because both copies are correct on the day they are written.
   *
   * The verbs are the host's because only the host can route, read a form or ask
   * the door again; the component decides where a node SITS and nothing else.
   * One hop per explicit action, never a crawl — `useGraphHops` owns that and is
   * not re-entered by expanding a window.
   */
  const graphWorkspace = (
        <RelationshipGraph
          hops={graph.hops}
          layout={layout}
          selected={selectedNode}
          onSelect={setSelectedNode}
          onPeek={(node) => setPeeking({ entity: node.entity, id: node.id })}
          peek={peeking ? (
            <PeekRow entity={peeking.entity} id={peeking.id}
              recipe={peekRecipeFor(peeking.entity)}
              testId="graph-peek-panel"
              onOpenRecord={() => onOpenRecord?.(peeking.entity, peeking.id)} />
          ) : undefined}
          onOpen={(node) => onOpenRecord?.(node.entity, node.id, node.label ?? undefined)}
          onExpand={(node) => graph.expand(node.entity, node.id)}
          onCollapse={(node) => graph.collapse(node.entity, node.id)}
          onMakeRoot={(node) => {
            graph.makeRoot(node.entity, node.id)
            // The preview belonged to the OLD graph. Leaving it open would put a
            // record's preview beside a graph that is no longer about it.
            setPeeking(null)
            setSelectedNode(null)
          }}
          onResetRoot={graph.root.id === id && graph.root.entity === entity
            ? undefined
            : () => { graph.makeRoot(entity, id); setPeeking(null); setSelectedNode(null) }}
          onShowMore={(slot) => {
            const group = graph.hops[0]?.groups.find((g) => g.slot === slot)
            if (group?.cursor != null) graph.showMore(slot, group.cursor)
          }}
          hiddenGroups={workspace.hidden}
          onToggleGroup={workspace.toggleGroup}
          /* The Levels cards (#2406): the governed glyph, the governed label of
             an edge fact, and Add only where the slot's own capability offers a
             write — `slotOffersAdd`, the same predicate the List plates read
             (#2849; before it, List offered Add unconditionally), and the same
             workspace it opens. A slot of a record two hops away is not
             one of this page's slots, so its facts fall back to the key. */
          iconOf={iconOf}
          describeFact={(slotId, key, value) => {
            const attribute = slotById.get(slotId)?.attributes.find((a) => a.key === key)
            if (!attribute) return null
            const option = attribute.options.find((o) => o.value === value)
            return { label: attribute.label, value: option?.label ?? String(value) }
          }}
          canAdd={offersAdd}
          onAdd={(slotId) => setAdding((a) => ({ slot: slotId, n: a.n + 1 }))}
          onAddRelationship={anyAdd
            ? () => setAdding((a) => ({ slot: '', n: a.n + 1 }))
            : undefined}
          actions={switchers}
          busy={graph.busy}
          problem={graph.problem ?? undefined}
        />
  )

  const gate = queryGate([schema], 'the relationships on this record', [counts])

  return (
    <div className={s.cascade} data-testid="relation-cascade">
      {gate}
      {/* No ⌘ here: the record's palette button sits left of the record's TITLE
          (Christopher's canvas model — "each form always has a palette button on
          top, left of the title of the form"), and it already offers every
          relationship as a verb. A second mark on this panel would be the same
          scope twice. Each related GRID keeps its own, which is a different
          scope: that child list. */}
      {!gate && !editing && mode === 'graph' ? (
        expanded ? (
          /* FULL SCREEN — the same element, in the shared `Modal`: Escape and an
             outside click close it, focus is trapped while it is open, and
             `restoreFocusTo` returns focus to the inline strip's Expand button,
             which is the control that SURVIVES the dialog closing. The button
             inside the dialog does not: it is unmounted with it, which is the
             defect `UserMenu`'s `triggerRef` exists for one surface over. */
          <Modal label="Relationships graph, full screen" variant="center"
            className={s.graphFullPanel} testid="relationship-graph-fullscreen"
            restoreFocusTo={expandBtn}
            onClose={() => setExpanded(false)}>
            {graphWorkspace}
          </Modal>
        ) : graphWorkspace
      ) : !gate && editing ? (
        <PersonalizeRelated entity={entity} entityLabel={label ?? 'this record'} slots={slots}
          restoreFocusTo={personalize} onClose={() => setEditing(false)} />
      ) : !gate && shown.map((section, i) => (
        <Connections
          key={section.label}
          presentation="workspace"
          /* The platform's own word for its one group is not a name anybody
             chose, so it is not presented as one. A section the PERSON made is. */
          heading={shown.length > 1 ? section.label : undefined}
          testId={i === 0 ? 'connections' : `connections-${i}`}
          groups={section.groups}
          /* `declared` is descriptive only. Saved order remains order: the
             server has no separate pin bit, so this host must not invent one. */
          arrangement={{
            declared: section.slots
              .filter((slot) => declaredPositions(slots, declarations).get(slot.id) != null)
              .map((slot) => slot.id),
          }}
          /* One lens PER RELATIONSHIP, so one control per relationship —
             in the header of the section it changes. The record-wide toggle
             this replaces could not express two slots reaching the same entity
             reading differently, and changing it moved every section at once. */
          lensOf={(key) => {
            const one = presentation.of(key)
            const drawn = drawnLens(key)
            /* The PROVENANCE travels now (#2349 §D3). The door has answered it
               per slot since #2276 — which rung decided, and whether this
               person's request was honoured or REFUSED — and the surface threw
               it away, so a refusal looked exactly like a success. It is the one
               state a selector must never hide. */
            return {
              active: drawn,
              available: one.available,
              source: one.provenance.activeSource,
              rung: one.provenance.decidingRung,
            }
          }}
          /* No per-plate icon strip (ruling 80629): the reading is the one
             switch above, and a plate's special readings live in its view
             chip's menu. */
          /* The canvas, supplied by the host because only the host can turn a
             group's rows into pins — and drawn INSIDE the section whose rows
             they are. A group whose target declares no recipe gets no canvas,
             which is the host saying "I cannot draw this", the one refusal that
             cannot be wrong. */
          renderMap={(group) => {
            const one = presentation.of(group.key)
            if (!one.mapCapable || !one.map) return null
            return (
              <RelationMap
                recordId={id}
                groups={[group]}
                slotById={slotById}
                recipes={{ [one.target]: one.map }}
                onOpenRecord={onOpenRecord}
              />
            )
          }}
          /* A Timeline is a reading of ONE relationship slot, so the server
             receives the slot identity and its governed recipe. The ordinary
             rows remain below it (Connections owns that invariant), retaining
             names, inline edits and Add while the chart supplies comparison. */
          renderTimeline={(group) => {
            const one = presentation.of(group.key)
            if (!one.timelineCapable || !one.timeline) return null
            return (
              <RelationTimeline entity={entity} recordId={id} slot={group.key}
                target={one.target} recipe={one.timeline} />
            )
          }}
          actions={i === 0 ? <div className={s.surfaceActions}>
            {switchers}
            {/* ONE action for the whole act (#2351). It used to be a chooser
                with two buttons that threw the type away the moment it was
                used, then either a lookup dialog or a jump out to the drawer.
                The workspace is the same governed reads and the same governed
                write door, on one surface. */}
            {anyAdd && (
              <Button size="sm" variant="primary"
                data-testid="add-relationship"
                onClick={() => setAdding((a) => ({ slot: '', n: a.n + 1 }))}>
                <Plus size={14} aria-hidden /> Add relationship
              </Button>
            )}
            {/* Contextual editing belongs in the header of the surface it
               changes. It used to occupy a detached, otherwise-empty strip
               above Connections, which made it look like page chrome rather
               than this record's related-layout preference. */}
            <CustomizeRowMenu triggerRef={personalize}
              label={`Relationships${order.isCustomised ? ' — customised' : ''}`}
              actions={[{
                key: 'customize-relationships', label: 'Customize relationships',
                onSelect: () => setEditing(true),
              }]} />
          </div> : undefined}
          /* KNOWN empty, and only known: the counts door answered, and it
             answered zero. An ABSENT entry is a slot the server derived no
             count for or refused to read, and drawing “nothing here yet” over
             rows RLS is hiding is what `useRelatedCounts` forbids in as many
             words. Those keep the ordinary body and its spinner. */
          isGroupEmpty={(group) => bySlot.get(group.key)?.n === 0 && group.items.length === 0}
          onOpen={(item) => onOpenRecord?.(item.entity, item.id, item.title ?? undefined)}
          /* Add, per relationship (#2406, leading design: "+ Add address" on
             each plate). The SAME workspace the record-level action opens,
             opened on this slot — the rail is still there, so changing type is
             one click and there is one flow, not two. */
          onAdd={(group) => setAdding((a) => ({ slot: group.key, n: a.n + 1 }))}
          /* …only where the slot truthfully offers a write (#2849): a
             relationship onto an entity written through its module's own door
             has no generic create, and an fk one no generic link, so neither
             its plate nor its empty row carries an Add core would refuse. */
          addOffered={(group) => offersAdd(group.key)}
          /* The governed SINGULAR ("Add address"), as the workspace's own "New
             address" already reads it; a group label is the plural. */
          addLabelOf={(group) => {
            const target = slotById.get(group.key)?.otherEntity
            const singular = (identities.data ?? []).find((i) => i.entity_type === target)?.singular_label
            return singular ? `Add ${singular.toLowerCase()}` : undefined
          }}
          renderGroupTools={(group) => {
            const slot = slotById.get(group.key)
            return slot ? (
              <RelationViewChip slot={slot} viewId={viewBySlot[slot.id] ?? ''}
                onSelect={(viewId) => setViewBySlot((current) => ({ ...current, [slot.id]: viewId }))}
                readings={(() => {
                  /* The special readings this relationship offers AND this
                     host can draw, beside "follow the switch". */
                  const one = presentation.of(slot.id)
                  const specials = one.available.filter((l) =>
                    (l === 'map' && one.mapCapable && one.map)
                    || (l === 'timeline' && one.timelineCapable && one.timeline)
                    || l === 'peek')
                  if (specials.length === 0) return undefined
                  const base = surfaceRead ?? 'grid'
                  const special = one.active !== 'grid' && one.active !== 'cards'
                  const chosen = special && (one.provenance.activeSource === 'requested' || surfaceRead == null)
                    ? one.active : 'base'
                  return {
                    options: [
                      { value: 'base', label: base === 'cards' ? 'Cards' : 'Grid' },
                      ...specials.map((l) => ({ value: l, label: LENS_WORD[l] ?? l })),
                    ],
                    active: chosen,
                    onChange: (v: string) => setLens(slot.id, (v === 'base' ? base : v) as RelatedPresentation),
                  }
                })()} />
            ) : null
          }}
          renderGroupBody={bodyOf}
          problem={connections.problem ?? undefined}
          empty={slots.length > 0
            ? 'No related records yet.'
            : 'No relationships are registered for this record type yet.'}
        />
      ))}
      {!gate && adding.n > 0 && (
        /* Kept outside the visible groups: ⌘K may target a hidden-zero slot,
           and starting the first relationship must not require showing an empty
           section first. `adding.slot` is the slot a verb asked for, or '' for
           the record-level action — which opens on the rail with nothing
           chosen, exactly as the mockup does. */
        <RelationshipWorkspace
          key={adding.n}
          slots={slots}
          entity={entity}
          recordId={id}
          recordLabel={label}
          initialSlot={adding.slot || null}
          onClose={() => setAdding({ slot: '', n: 0 })}
        />
      )}
    </div>
  )
}

/**
 * One slot's ROWS — the relationship grid, and nothing around it.
 *
 * Its own component because each needs its own `useRelationRows`, and hooks
 * cannot be called in a loop. Split out from `RegisteredRelationSection` so the
 * record page can put the same grid inside the shared component's section
 * without the second header that would come with it.
 */
export function RelationSectionBody({
  slot, recordId, parentEntity, parentLabel, read = true, countKnown = true, lens, peekRecipe,
  bare = false, viewId: chosenView, onOpenRecord,
}: {
  slot: RelationSlot
  recordId: string
  parentEntity: string
  parentLabel?: string
  /**
   * `false` when the server already said this slot is empty. `BandGrid` then
   * renders its empty state from no rows, which is the same answer one round
   * trip cheaper — and the read is the thing that has to be justified, not the
   * section.
   */
  read?: boolean
  /** False only when the count door supplied no evidence for this slot. */
  countKnown?: boolean
  /**
   * Metro and MAP are drawn by Connections and by the map canvas above; a
   * section's own rows fall back to the dense grid under both.
   *
   * That fallback is the mixed-workspace clause where a reader actually meets
   * it: in the map lens every group still renders its section, and a section is
   * where the rows, the inline edge edits and the Add live — none of which has a
   * home on a canvas.
   */
  lens?: RelatedPresentation
  /** The target's declared preview recipe, for the peek lens. */
  peekRecipe?: PeekRecipe | null
  /**
   * The grid ALONE, in a plate that already carries its name, count, view and
   * Add (#2406, leading design — the record page). Without it the rows keep
   * their own collection bar, which the CRM page's tabbed sections still need.
   */
  bare?: boolean
  /** The saved view, when the host's header chooses it (the plate's chip). */
  viewId?: string
  onOpenRecord?: (entity: string, id: string, name?: string) => void
}) {
  const [openRow, setOpenRow] = useState<string | null>(null)
  const [presentation, setPresentation] = useState<'grid' | 'cards'>(lens === 'cards' ? 'cards' : 'grid')
  /* The grid reflowed to cards: header and column ⋮ are inert (#2375). */
  const [headersHidden, setHeadersHidden] = useState(false)
  const related = useRelationRows(read ? slot : null, recordId)
  const defaultShape = useDefaultColumns(slot.otherEntity, 4)
  const savedViews = useViews(slot.otherEntity)
  const viewRows = useMemo(() => (savedViews.data ?? []) as RelationView[], [savedViews.data])
  /* The relationship's saved view, SHARED across parent records (ADR F-84 §4):
     keyed by the parent entity TYPE and the slot, never by `recordId`. */
  const [ownView, setViewId] = useNestedViewChoice(parentEntity, slot.id)
  const viewId = chosenView ?? ownView
  const activeView = viewRows.find((view) => String(view.id) === viewId) ?? viewRows[0]
  /* The twin of `RelationBand`'s, and now literally the same code
     (`relationCollection.tsx`). A section's slot never changes, so only a
     change of view starts it clean — which this copy never did before. */
  const collection = useRelationCollection({
    related,
    slot,
    recordId,
    defaultColumns: defaultShape.columns,
    activeView,
    resetKey: viewId,
    sessionKey: `${parentEntity}:${recordId}#${slot.id}`,
    labelOf: (key) => key === RECORD_COLUMN
      ? slot.otherLabel
      : key === '__validity' ? 'Validity'
        : String(slot.attributes.find((attribute) => attribute.key === key)?.label
          ?? defaultShape.fields.get(key)?.label ?? key),
  })
  const { search, setSearch } = collection
  const rowItems = useMemo(
    () => related.rows.map(({ record }) => ({ id: String(record.id), label: displayLabel(record) })),
    [related.rows],
  )

  useEffect(() => { setPresentation(lens === 'cards' ? 'cards' : 'grid') }, [lens])

  const grid = (
    <BandGrid slot={slot} recordId={recordId} parentEntity={parentEntity} parentLabel={parentLabel}
      depth={0} path={[`${parentEntity}:${recordId}`]}
      openRow={openRow} setOpenRow={setOpenRow} trail={[slot.label]}
      related={collection.visibleRelated} countKnown={search || collection.grid.filter ? true : countKnown}
      /* Peek keeps the COMPACT grid in view — that is its whole point, and the
         reason it is a lens over the rows rather than a replacement for them. */
      presentation={presentation} {...collection.grid} onHeadersHiddenChange={setHeadersHidden}
      peek={lens === 'peek'} peekRecipe={peekRecipe}
      onOpenRecord={onOpenRecord} />
  )

  /* THE PLATE IS THE BAR (#2406, leading design). On the record page every
     control the collection bar carried already has a home: the name, the count
     and Add are the plate's header; the view is its chip; the lens is its
     selector; sort, filter and columns are each column's ⋮ menu; the surface's
     filter narrows the plates. What is left is the grid, and the one thing a
     column menu cannot show — the filter in force, as a removable chip. */
  if (bare) {
    return (
      <>
        {collection.filters}
        {collection.notice}
        {grid}
        {collection.menus}
      </>
    )
  }

  return (
    <>
      <CollectionWorkspace identity={`form-related:${parentEntity}:${slot.id}`} depth={1}
        testId={`relation-section-bar-${slot.id}`}
        onCommand={() => openPalette({
          entity: slot.otherEntity,
          label: `${slot.label} of ${parentLabel ?? 'this record'}`,
          items: rowItems,
          ...(relationshipWriteOffered(slot, 'create') ? { create: {
            label: `New ${slot.otherLabel.toLowerCase()} - linked to ${parentLabel ?? 'this record'}`,
            run: () => openQuickCreate({ slot, recordId, recordLabel: parentLabel }),
          } } : {}),
        })}
        commandLabel={`Command palette for ${slot.label} of ${parentLabel ?? 'this record'}`}
        /* The twin of `RelationBand`'s (#2406 section 4). At depth > 0 the
           selector carries the RELATIONSHIP's name when it has no view to name
           — the bar has no title slot any more (#2375). */
        viewSelector={(
          <SavedViewSelector
            views={viewRows.length > 0
              ? viewRows.map((view) => ({ id: String(view.id), name: String(view.name ?? 'View') }))
              : [{ id: 'default', name: slot.label }]}
            activeId={viewRows.length > 0 ? String(activeView?.id ?? '') : 'default'}
            subject={slot.label}
            modified={collection.settings.modified}
            onSelect={setViewId} disabled={viewRows.length === 0} />
        )}
        viewSettings={(
          <ViewSettingsMenu subject={slot.label.toLowerCase()} modified={collection.settings.modified}
            onReset={collection.settings.onReset}
            sort={headersHidden ? {
              columns: collection.columnVerbs.columns,
              active: collection.columnVerbs.sortActive,
              onSort: collection.grid.onSortColumn,
            } : undefined}
            group={headersHidden ? {
              columns: collection.columnVerbs.groupable,
              active: collection.columnVerbs.groupActive,
              onGroup: collection.columnVerbs.onGroup,
            } : undefined}
            onColumns={headersHidden ? collection.grid.onChooseColumns : undefined}
            presentation={{
              value: presentation,
              options: [
                { value: 'grid', label: 'Show as table', icon: <Table2 size={14} aria-hidden /> },
                { value: 'cards', label: 'Show as cards', icon: <LayoutGrid size={14} aria-hidden /> },
              ],
              onChange: (value) => setPresentation(value as 'grid' | 'cards'),
            }}
            testId={`relation-section-settings-${slot.id}`} />
        )}
        filters={collection.filters}
        notice={collection.notice}
        search={<GridSearch value={search} onChange={setSearch} collection={slot.label} />}
        /* The count only — see RelationBand's footer: the relType / direction
           / storage triple was a metamodel diagnostic (#2406 rogue-debt). */
        footer={<span className="tnum">{collection.visibleRelated.rows.length === related.rows.length
          ? `${related.rows.length} ${slot.label.toLowerCase()}`
          : `${collection.visibleRelated.rows.length} of ${related.rows.length} ${slot.label.toLowerCase()}`}</span>}>
        {grid}
      </CollectionWorkspace>
      {collection.menus}
    </>
  )
}

/**
 * One relationship's saved-view chip, in its plate's header (#2406, leading
 * design: "All addresses ▾").
 *
 * The same `SavedViewSelector` the collection bar carried, seated as a chip,
 * reading the same `useViews` (cached, so the grid under it pays nothing
 * twice). An entity with no saved view has nothing to choose, and a chip that
 * says "Default view" on every plate is noise — so it draws nothing there.
 */
const LENS_WORD: Partial<Record<RelatedPresentation, string>> = {
  map: 'Map', timeline: 'Timeline', peek: 'Preview',
}

function RelationViewChip({ slot, viewId, onSelect, readings }: {
  slot: RelationSlot
  viewId: string
  onSelect: (viewId: string) => void
  /** The relationship's special readings, beside its saved views (ruling 80629). */
  readings?: SavedViewSelectorProps['readings']
}) {
  const views = useViews(slot.otherEntity)
  const rows = (views.data ?? []) as RelationView[]
  if (rows.length === 0 && !readings) return null
  const active = rows.find((view) => String(view.id) === viewId) ?? rows[0]
  return (
    <SavedViewSelector appearance="chip"
      views={rows.map((view) => ({ id: String(view.id), name: String(view.name ?? 'View') }))}
      activeId={String(active?.id ?? '')}
      subject={slot.label}
      testId={`relation-view-${slot.id}`}
      readings={readings}
      onSelect={onSelect} />
  )
}

/**
 * One slot with its OWN header — the CRM record page's tabbed sections
 * (`crm/RecordPage.tsx`), where a section is chosen by a tab and therefore has
 * to name itself. The record page's cascade does not use this: there the
 * heading, the count and the Add belong to the shared `Connections` section.
 */
export function RegisteredRelationSection({
  slot, recordId, parentEntity, parentLabel, count = null, defaultOpen = true,
  collapsible = false, onOpenRecord, title, description, openSignal = 0,
  testId = `relation-section-${slot.id}`, headTestId = `relation-head-${slot.id}`,
}: RegisteredRelationSectionProps) {
  /* DERIVED, not seeded. `useState(defaultOpen)` reads its argument on the FIRST
     render only — and on that render the counts have not arrived, so every slot
     looks uncountable, every section opened, and nothing ever corrected itself
     when the real numbers landed. The user's own toggle is an override on top of
     the derived value, so it still wins once they touch it. */
  const [override, setOverride] = useState<boolean | null>(null)
  const open = collapsible ? (override ?? defaultOpen) : true

  return (
    <section className={s.section} data-testid={testId} data-open={open || undefined}>
      <h3 className={s.head} data-testid={headTestId}>
        {collapsible ? <>
          <button type="button" className={s.toggle} aria-expanded={open}
            onClick={() => setOverride(!open)}>
            <span className={s.twist} aria-hidden="true">{open ? '−' : '+'}</span>
            <span className={s.label}>{title ?? slot.label}</span>
            {count !== null && <span className={s.n}>{count}</span>}
          </button>
          {/* OUTSIDE the button, deliberately. The definition's prose is a
              sentence; inside the toggle it would join the control's accessible
              name, so a screen reader would announce the whole description
              every time the section is expanded or collapsed. */}
          {description && <span className={s.description}>{description}</span>}
        </> : <>
          <span className={s.staticTitle}>{title ?? slot.label}</span>
          {description && <span className={s.description}>{description}</span>}
        </>}
        {/* Add-existing-or-create, per slot — the SAME workspace, opened on
            this slot. It lived only inside the tab panel this cascade replaced,
            so carrying it here is the difference between moving a surface and
            deleting a capability; opening the one workspace rather than a
            second picker is what keeps the two from drifting. */}
        <span className={s.add}>
          <SlotWorkspaceAdd slot={slot} recordId={recordId} parentEntity={parentEntity}
            parentLabel={parentLabel} openSignal={openSignal} />
        </span>
      </h3>
      {open && (
        <RelationSectionBody slot={slot} recordId={recordId} parentEntity={parentEntity}
          parentLabel={parentLabel} onOpenRecord={onOpenRecord} />
      )}
    </section>
  )
}

/**
 * One slot's Add, inside a section that owns its own heading.
 *
 * It opens the ONE workspace on that slot rather than a second picker: the rail
 * is still there, so switching type from here is the same act with the same
 * discard question, and the two surfaces cannot drift because there is only
 * one. The slots come from the PARENT entity's metamodel — the same derivation
 * the record-level action uses, not a one-element list, because a person who
 * opened "People" and then wants "Addresses" should not have to close anything.
 */
function SlotWorkspaceAdd({
  slot, recordId, parentEntity, parentLabel, openSignal = 0,
}: {
  slot: RelationSlot
  recordId: string
  parentEntity: string
  parentLabel?: string
  openSignal?: number
}) {
  const schema = useSchema()
  const slots = useMemo(() => listSlots(schema.data, parentEntity), [schema.data, parentEntity])
  const [open, setOpen] = useState(0)

  useEffect(() => { if (openSignal > 0) setOpen((n) => n + 1) }, [openSignal])

  return (
    <>
      <Button variant="ghost" size="sm" data-testid={`rel-add-${slot.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((n) => n + 1) }}>
        <Plus size={14} aria-hidden /> Add {slot.otherLabel.toLowerCase()}
      </Button>
      {open > 0 && (
        <RelationshipWorkspace
          key={open}
          slots={slots.length > 0 ? slots : [slot]}
          entity={parentEntity}
          recordId={recordId}
          recordLabel={parentLabel}
          initialSlot={slot.id}
          onClose={() => setOpen(0)}
        />
      )}
    </>
  )
}
